import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../utils/common/cn";

/** One row of the Weighted Average Life table -- an industry, or (as a
 *  subRow) one office within it. Mirrors segments-table.tsx's SegmentNode
 *  shape (a flat node type with an optional subRows array), simplified to
 *  the three columns this table actually shows. */
export interface WalNode {
  id: string;
  segment: string;
  segmentWal: number;
  portfolioWal: number;
  subRows?: WalNode[];
}

const helper = createColumnHelper<WalNode>();

/** Collapsible like Segments' own "Segment Economics" table
 *  (segments-table.tsx): same getSubRows/getExpandedRowModel wiring, same
 *  chevron + depth-indent convention for the row-label column. */
export function WalTable({ rows }: { rows: WalNode[] }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const columns: ColumnDef<WalNode, any>[] = [
    helper.display({
      id: "segment",
      header: "Segment",
      cell: ({ row }) => {
        const canExpand = row.getCanExpand();
        return (
          <div className="flex items-center gap-2" style={{ paddingLeft: `${row.depth * 18}px` }}>
            {canExpand ? (
              <button
                type="button"
                onClick={row.getToggleExpandedHandler()}
                aria-label={row.getIsExpanded() ? "Collapse Row" : "Expand Row"}
                className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="size-3" />
                ) : (
                  <ChevronRight className="size-3" />
                )}
              </button>
            ) : (
              <span className="size-5" />
            )}
            <span
              className={cn(
                "text-sm",
                row.depth === 0 ? "font-medium text-foreground" : "text-foreground/90",
              )}
            >
              {row.original.segment}
            </span>
          </div>
        );
      },
    }),
    helper.accessor("segmentWal", {
      header: "Segment WAL",
      cell: (info) => info.getValue().toFixed(2),
    }),
    helper.accessor("portfolioWal", {
      header: "Portfolio WAL",
      cell: (info) => (
        <span className="text-muted-foreground">{info.getValue().toFixed(2)}</span>
      ),
    }),
  ];

  const table = useReactTable({
    data: rows,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="border-b border-border bg-secondary/50">
              {group.headers.map((header, i) => (
                <th
                  key={header.id}
                  className={cn(
                    "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                    i === 0 ? "text-left" : "text-right",
                  )}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-b border-border/60 last:border-b-0">
              {row.getVisibleCells().map((cell, i) => (
                <td
                  key={cell.id}
                  className={cn("px-3 py-2.5 tabular-nums", i === 0 ? "" : "text-right")}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
